import { useGet } from '@lovoldsystem/core.client'
import { IFacilityWithTypes } from 'src/generated-types/facility'
import { IMainComponent } from 'src/generated-types/main-component'
import { IStorage, IStorageWithPermission } from 'src/generated-types/storage-types'
import { queryClient } from 'src/query-client'
import { mainComponentQueryKeys } from 'src/query-keys'
import { useDelete, usePost, usePut } from './reactQuery'
import i18n from 'src/i18n'

const baseUrl = (customerId: EntityId) => `customers/${customerId}/storages`

export const storageEndPoints = {
  getStorages(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  getStorage(customerId: EntityId, storageId: EntityId) {
    return `${baseUrl(customerId)}/${storageId}`
  },
  createStorage(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  updateStorage(customerId: EntityId, storageId: EntityId) {
    return `${baseUrl(customerId)}/${storageId}`
  },
  deleteStorage(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  storagesForDropDown(customerId: EntityId) {
    return `${baseUrl(customerId)}/access-control-drop-down`
  },
  getStorageById(customerId: EntityId, storageId: EntityId) {
    return `${baseUrl(customerId)}/${storageId}`
  },
  getEquipmentInStorage(customerId: EntityId, storageId: EntityId) {
    return `${baseUrl(customerId)}/${storageId}/equipment`
  },
  getAllStorageWithCustomers(customerId: EntityId) {
    return `${baseUrl(customerId)}/drop-down`
  },
  getStoragesMinimalData(customerId: EntityId) {
    return `${baseUrl(customerId)}/minimal`
  },
}

export const invalidateStorageCache = {
  getStorages(customerId: EntityId) {
    queryClient.invalidateQueries(storageEndPoints.getStorages(customerId))
  },
  getStorage(customerId: EntityId, storageId: EntityId) {
    queryClient.invalidateQueries(storageEndPoints.getStorage(customerId, storageId))
  },
  getEquipmentInStorage(customerId: EntityId, storageId: EntityId) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_STORAGE_EQUIPMENT_LIST(customerId, storageId))
  },
  getAllStorageWithCustomers(customerId: EntityId) {
    queryClient.invalidateQueries(storageEndPoints.getAllStorageWithCustomers(customerId))
  },
  getStorageForDropDown(customerId: EntityId) {
    queryClient.invalidateQueries(storageEndPoints.storagesForDropDown(customerId))
  },
  getStoragesMinimalData(customerId: EntityId) {
    queryClient.invalidateQueries(storageEndPoints.getStoragesMinimalData(customerId))
  },
}

export const useGetStorages = (customerId: EntityId) =>
  useGet<Array<IStorage>>(storageEndPoints.getStorages(customerId))

export const useGetStoragesByClient = ({ clientId }: { clientId: EntityId }) =>
  useGet<Array<IStorage>>(storageEndPoints.getStorages(clientId))

export const useGetStorage = (customerId: EntityId, storageId: EntityId) =>
  useGet<IStorageWithPermission>(storageEndPoints.getStorage(customerId, storageId))

export const useGetStorageForDropDown = ({ clientId }: { clientId: EntityId }) =>
  useGet<IFacilityWithTypes>(storageEndPoints.storagesForDropDown(clientId))

export const useCreateStorages = (customerId: EntityId) =>
  usePost<IStorage, any>(storageEndPoints.createStorage(customerId))

export const useUpdateStorages = (customerId: EntityId, storageId: EntityId) =>
  usePut<IStorage, any>(storageEndPoints.updateStorage(customerId, storageId))

export const useDeleteStorages = (customerId: EntityId) =>
  useDelete<IStorage>(storageEndPoints.deleteStorage(customerId))

export const useGetStorageById = (customerId: EntityId, storageId: EntityId) =>
  useGet<IStorage>(storageEndPoints.getStorageById(customerId, storageId))

export const useGetEquipmentInStorage = (customerId: EntityId, storageId: EntityId, search?: string) =>
  useGet<Array<IMainComponent>>(
    storageEndPoints.getEquipmentInStorage(customerId, storageId),
    { search: search ?? '', selectedLanguage: i18n.language },
    {
      queryKey: mainComponentQueryKeys.GET_STORAGE_EQUIPMENT_LIST(customerId, storageId, search, i18n.language),
    },
  )

export const useGetAllStorageWithCustomers = (customerId: EntityId, search?: string) =>
  useGet<IStorage[]>(storageEndPoints.getAllStorageWithCustomers(customerId), { search })

export const useGetStoragesMinimalData = (customerId: EntityId) =>
  useGet<IStorage[]>(storageEndPoints.getStoragesMinimalData(customerId))
